import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../../services/adminApi';
import SectionManager from './SectionManager';
import { getFlagUrl, renderCountryFlag } from '../../utils/flagUtils';

interface ServicePage {
  id?: number;
  slug: string;
  status: 'draft' | 'published' | 'archived';
  display_order: number;
  menu_position: 'main' | 'services' | 'footer' | 'none';
  is_featured: boolean;
  template_type: string;
  country_id: number | null;
}

interface ServicePageContent {
  language: 'en' | 'ar';
  hero_title: string;
  hero_subtitle: string;
  hero_description: string;
  hero_cta_text: string;
  hero_cta_link: string;
  hero_image: string;
  hero_bg_color_primary: string;
  hero_bg_color_secondary: string;
  hero_gradient_direction: string;
  hero_text_color: string;
  hero_overlay_opacity: number;
  hero_pattern: string;
  hero_animation_speed: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_title: string;
  og_description: string;
  og_image: string;
  content_summary: string;
  // Landing card customization
  card_gradient_from: string;
  card_gradient_to: string;
  card_icon: string;
  card_features: string[];
  card_description: string;
}

interface Country {
  id: number;
  name: string;
  name_ar: string;
  code: string;
  flag_emoji: string;
}

interface SectionItem {
  id?: number;
  item_key: string;
  display_order: number;
  is_active: boolean;
  content: {
    en: {
      title: string;
      description: string;
      metadata?: any;
    };
    ar: {
      title: string;
      description: string;
      metadata?: any;
    };
  };
  icon?: string;
  config?: any;
}

interface Section {
  id?: number;
  section_type: string;
  section_key: string;
  display_order: number;
  is_active: boolean;
  content: {
    en: {
      title: string;
      subtitle: string;
      content: string;
    };
    ar: {
      title: string;
      subtitle: string;
      content: string;
    };
  };
  items: SectionItem[];
  config?: any;
}

interface ServicePageModalProps {
  isOpen: boolean;
  onClose: () => void;
  page?: ServicePage | null;
  onSave: (pageData: any) => Promise<void>;
  countries: Country[];
}

const ServicePageModal: React.FC<ServicePageModalProps> = ({
  isOpen,
  onClose,
  page,
  onSave,
  countries
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'content-en' | 'content-ar' | 'hero-design' | 'landing-card' | 'sections' | 'seo'>('basic');
  const [sectionLanguage, setSectionLanguage] = useState<'en' | 'ar'>('en');
  const [cardLanguage, setCardLanguage] = useState<'en' | 'ar'>('en');

  // Card gradient color states (hex colors for custom RGB picker)
  const [cardPrimaryColor, setCardPrimaryColor] = useState('#3B82F6');
  const [cardSecondaryColor, setCardSecondaryColor] = useState('#0EA5E9');

  // Validation states
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Basic page data
  const [pageData, setPageData] = useState<ServicePage>({
    slug: '',
    status: 'draft',
    display_order: 1,
    menu_position: 'services',
    is_featured: false,
    template_type: 'service',
    country_id: null
  });

  // Content data for both languages
  const [contentEn, setContentEn] = useState<ServicePageContent>({
    language: 'en',
    hero_title: '',
    hero_subtitle: '',
    hero_description: '',
    hero_cta_text: 'Get Started Today',
    hero_cta_link: '/register',
    hero_image: '',
    hero_bg_color_primary: '#3B82F6',
    hero_bg_color_secondary: '#1E40AF',
    hero_gradient_direction: 'to-r',
    hero_text_color: '#FFFFFF',
    hero_overlay_opacity: 0.10,
    hero_pattern: 'circles',
    hero_animation_speed: 'normal',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    content_summary: '',
    card_gradient_from: '#3B82F6',
    card_gradient_to: '#06B6D4',
    card_icon: '🏢',
    card_features: ['Company Registration', 'Business License', 'Tax Registration', 'Bank Account Support'],
    card_description: ''
  });

  const [contentAr, setContentAr] = useState<ServicePageContent>({
    language: 'ar',
    hero_title: '',
    hero_subtitle: '',
    hero_description: '',
    hero_cta_text: 'ابدأ اليوم',
    hero_cta_link: '/register',
    hero_image: '',
    hero_bg_color_primary: '#3B82F6',
    hero_bg_color_secondary: '#1E40AF',
    hero_gradient_direction: 'to-r',
    hero_text_color: '#FFFFFF',
    hero_overlay_opacity: 0.10,
    hero_pattern: 'circles',
    hero_animation_speed: 'normal',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    content_summary: '',
    card_gradient_from: '#3B82F6',
    card_gradient_to: '#06B6D4',
    card_icon: '🏢',
    card_features: ['تسجيل الشركة', 'الترخيص التجاري', 'التسجيل الضريبي', 'دعم الحساب المصرفي'],
    card_description: ''
  });

  // Page sections
  const [sections, setSections] = useState<Section[]>([]);

  // Initialize form when page prop changes
  useEffect(() => {
    if (page) {
      setPageData({
        id: page.id,
        slug: page.slug,
        status: page.status,
        display_order: page.display_order,
        menu_position: page.menu_position,
        is_featured: page.is_featured,
        template_type: page.template_type,
        country_id: page.country_id
      });
      // Load existing content when editing
      if (page.id) {
        loadExistingPageContent(page.id);
      }
    } else {
      // Reset form for new page
      setPageData({
        slug: '',
        status: 'draft',
        display_order: 1,
        menu_position: 'services',
        is_featured: false,
        template_type: 'service',
        country_id: null
      });
    }
    // Reset validation state when page changes
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [page]);

  // Validate sections and business logic when they change (but only if they were touched by submit attempt)
  useEffect(() => {
    if (isSubmitting || Object.keys(touched).some(key => key.startsWith('section_') || ['slug', 'country_id', 'menu_position', 'display_order', 'is_featured', 'status'].includes(key))) {
      const sectionErrors = validateSections();
      const businessErrors = validateBusinessLogic();

      setErrors(prev => {
        const newErrors = { ...prev };

        // Remove old section and business logic errors
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('section_') || key === 'duplicate_keys' ||
              ['content_consistency', 'slug_seo_length', 'main_menu_order', 'featured_unpublished',
               'hero_not_first', 'cta_not_last', 'duplicate_hero', 'duplicate_packages', 'duplicate_cta',
               'cta_missing_link', 'cta_missing_text'].includes(key)) {
            delete newErrors[key];
          }
        });

        // Add new section errors
        Object.entries(sectionErrors).forEach(([key, error]) => {
          if (error) {
            newErrors[key] = error;
          }
        });

        // Add new business logic errors
        Object.entries(businessErrors).forEach(([key, error]) => {
          if (error) {
            newErrors[key] = error;
          }
        });

        return newErrors;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, pageData, contentEn, contentAr, isSubmitting, touched]);

  // Validation functions
  const validateSlug = (slug: string): string => {
    if (!slug) return 'URL slug is required';
    if (slug.length < 3) return 'URL slug must be at least 3 characters';
    if (slug.length > 100) return 'URL slug must be no more than 100 characters';
    if (!/^[a-z0-9-]+$/.test(slug)) return 'URL slug can only contain lowercase letters, numbers, and hyphens';
    if (slug.startsWith('-') || slug.endsWith('-')) return 'URL slug cannot start or end with a hyphen';
    if (slug.includes('--')) return 'URL slug cannot contain consecutive hyphens';
    return '';
  };

  const validateDisplayOrder = (order: number): string => {
    if (order < 0) return 'Display order must be 0 or greater';
    if (order > 999) return 'Display order must be less than 1000';
    return '';
  };

  const validateContentField = (value: string, fieldName: string, isRequired = false, minLength = 0, maxLength = 1000): string => {
    if (isRequired && !value.trim()) return `${fieldName} is required`;
    if (value && value.length < minLength) return `${fieldName} must be at least ${minLength} characters`;
    if (value && value.length > maxLength) return `${fieldName} must be no more than ${maxLength} characters`;
    return '';
  };

  const validateUrl = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('#')) return '';
    return 'CTA link must be a valid URL (starting with /, #, http://, or https://)';
  };

  const validateSeoField = (value: string, fieldName: string, recommendedLength?: number): string => {
    if (value && recommendedLength && value.length > recommendedLength * 1.5) {
      return `${fieldName} is too long (recommended: ${recommendedLength} characters)`;
    }
    return '';
  };

  // Section validation functions
  const validateSections = (): Record<string, string> => {
    const sectionErrors: Record<string, string> = {};

    // Define section types with their validation requirements
    const sectionTypes = {
      hero: { hasItems: false, requiresTitle: true },
      benefits: { hasItems: true, requiresTitle: true },
      sectors: { hasItems: true, requiresTitle: true },
      business_types: { hasItems: true, requiresTitle: true },
      packages: { hasItems: false, requiresTitle: true },
      requirements: { hasItems: true, requiresTitle: true },
      process: { hasItems: true, requiresTitle: true },
      faq: { hasItems: true, requiresTitle: true },
      states: { hasItems: true, requiresTitle: true },
      cta: { hasItems: false, requiresTitle: true }
    };

    // Check for duplicate section keys
    const sectionKeys = sections.map(s => s.section_key);
    const duplicateKeys = sectionKeys.filter((key, index) => sectionKeys.indexOf(key) !== index);
    if (duplicateKeys.length > 0) {
      sectionErrors.duplicate_keys = `Duplicate section keys found: ${duplicateKeys.join(', ')}`;
    }

    sections.forEach((section, sectionIndex) => {
      const sectionType = sectionTypes[section.section_type as keyof typeof sectionTypes];
      const sectionPrefix = `section_${sectionIndex}`;

      if (!sectionType) {
        sectionErrors[`${sectionPrefix}_type`] = `Invalid section type: ${section.section_type}`;
        return;
      }

      // Validate section key
      if (!section.section_key || section.section_key.trim().length === 0) {
        sectionErrors[`${sectionPrefix}_key`] = 'Section key is required';
      } else if (!/^[a-z0-9_-]+$/.test(section.section_key)) {
        sectionErrors[`${sectionPrefix}_key`] = 'Section key can only contain lowercase letters, numbers, underscores, and hyphens';
      }

      // Validate display order
      if (section.display_order < 1) {
        sectionErrors[`${sectionPrefix}_order`] = 'Display order must be 1 or greater';
      }

      // Validate section titles (at least one language should have content)
      const hasEnTitle = section.content?.en?.title && section.content.en.title.trim().length > 0;
      const hasArTitle = section.content?.ar?.title && section.content.ar.title.trim().length > 0;

      if (sectionType.requiresTitle && !hasEnTitle && !hasArTitle) {
        sectionErrors[`${sectionPrefix}_title`] = 'Section must have a title in at least one language';
      }

      // Validate items for sections that should have them
      if (sectionType.hasItems) {
        if (!section.items || section.items.length === 0) {
          // Only show warning for some section types, not error
          if (['benefits', 'sectors', 'requirements', 'process', 'faq'].includes(section.section_type)) {
            sectionErrors[`${sectionPrefix}_items_empty`] = `${section.section_type} section typically needs items to be useful`;
          }
        } else {
          // Validate each item
          section.items.forEach((item, itemIndex) => {
            const itemPrefix = `${sectionPrefix}_item_${itemIndex}`;

            // Validate item key
            if (!item.item_key || item.item_key.trim().length === 0) {
              sectionErrors[`${itemPrefix}_key`] = 'Item key is required';
            }

            // Validate item titles (at least one language should have content)
            const hasEnItemTitle = item.content?.en?.title && item.content.en.title.trim().length > 0;
            const hasArItemTitle = item.content?.ar?.title && item.content.ar.title.trim().length > 0;

            if (!hasEnItemTitle && !hasArItemTitle) {
              sectionErrors[`${itemPrefix}_title`] = 'Item must have a title in at least one language';
            }

            // Validate display order for items
            if (item.display_order < 1) {
              sectionErrors[`${itemPrefix}_order`] = 'Item display order must be 1 or greater';
            }
          });

          // Check for duplicate item keys within the section
          const itemKeys = section.items.map(item => item.item_key);
          const duplicateItemKeys = itemKeys.filter((key, index) => itemKeys.indexOf(key) !== index);
          if (duplicateItemKeys.length > 0) {
            sectionErrors[`${sectionPrefix}_duplicate_item_keys`] = `Duplicate item keys in section: ${duplicateItemKeys.join(', ')}`;
          }
        }
      } else {
        // For sections that shouldn't have items, warn if they do
        if (section.items && section.items.length > 0) {
          sectionErrors[`${sectionPrefix}_unexpected_items`] = `${section.section_type} section should not have items`;
        }
      }
    });

    return sectionErrors;
  };

  // Business logic validation functions
  const validateBusinessLogic = (): Record<string, string> => {
    const businessErrors: Record<string, string> = {};

    // 1. Country selection is optional for service pages
    // No validation needed since template_type is always 'service'

    // 2. Validate content consistency between languages
    const validateContentConsistency = (): string[] => {
      const warnings: string[] = [];

      // Check critical content fields that should have both languages
      const criticalFields = [
        { en: contentEn.hero_title, ar: contentAr.hero_title, name: 'Hero Title' },
        { en: contentEn.meta_title, ar: contentAr.meta_title, name: 'Meta Title' }
      ];

      criticalFields.forEach(field => {
        if (field.en && field.en.trim() && (!field.ar || !field.ar.trim())) {
          warnings.push(`${field.name}: English content exists but Arabic is missing`);
        }
        if (field.ar && field.ar.trim() && (!field.en || !field.en.trim())) {
          warnings.push(`${field.name}: Arabic content exists but English is missing`);
        }
      });

      return warnings;
    };

    const contentWarnings = validateContentConsistency();
    if (contentWarnings.length > 0) {
      businessErrors.content_consistency = contentWarnings.join('; ');
    }

    // 3. Validate slug uniqueness format for SEO
    if (pageData.slug) {
      // Check for common SEO issues
      if (pageData.slug.length > 50) {
        businessErrors.slug_seo_length = 'URL slug is too long for optimal SEO (recommended: under 50 characters)';
      }

      // No specific slug structure requirements for service pages
    }

    // 4. Validate menu position logic
    if (pageData.menu_position === 'main' && pageData.display_order > 5) {
      businessErrors.main_menu_order = 'Main menu items should have display order 1-5 for better navigation';
    }

    if (pageData.is_featured && pageData.status !== 'published') {
      businessErrors.featured_unpublished = 'Featured pages should be published to be visible to users';
    }

    // 5. Validate section business logic
    if (sections.length > 0) {
      // No specific section requirements for service pages

      // Check section order logic
      const heroSection = sections.find(s => s.section_type === 'hero');
      const ctaSection = sections.find(s => s.section_type === 'cta');

      if (heroSection && heroSection.display_order !== 1) {
        businessErrors.hero_not_first = 'Hero section should be first (display order 1) for optimal page structure';
      }

      if (ctaSection && ctaSection.display_order !== sections.length) {
        businessErrors.cta_not_last = 'Call-to-Action section should be last for better conversion';
      }

      // Check for duplicate section types that shouldn't be duplicated
      const uniqueSectionTypes = ['hero', 'packages', 'cta'];
      uniqueSectionTypes.forEach(type => {
        const sectionsOfType = sections.filter(s => s.section_type === type);
        if (sectionsOfType.length > 1) {
          businessErrors[`duplicate_${type}`] = `Only one ${type} section should be used per page`;
        }
      });
    }

    // 6. Validate CTA consistency
    const hasCtaContent = contentEn.hero_cta_text || contentAr.hero_cta_text;
    const hasCtaLink = contentEn.hero_cta_link || contentAr.hero_cta_link;

    if (hasCtaContent && !hasCtaLink) {
      businessErrors.cta_missing_link = 'CTA button text is provided but link is missing';
    }
    if (hasCtaLink && !hasCtaContent) {
      businessErrors.cta_missing_text = 'CTA link is provided but button text is missing';
    }

    return businessErrors;
  };

  const validateField = (fieldKey: string, value: any): string => {
    switch (fieldKey) {
      case 'slug':
        return validateSlug(value || '');
      case 'display_order':
        return validateDisplayOrder(Number(value) || 0);
      case 'hero_title_en':
        return validateContentField(value || '', 'English hero title', true, 5, 150);
      case 'hero_title_ar':
        return validateContentField(value || '', 'Arabic hero title', true, 5, 150);
      case 'hero_subtitle_en':
        return validateContentField(value || '', 'English hero subtitle', false, 0, 200);
      case 'hero_subtitle_ar':
        return validateContentField(value || '', 'Arabic hero subtitle', false, 0, 200);
      case 'hero_description_en':
        return validateContentField(value || '', 'English hero description', false, 0, 500);
      case 'hero_description_ar':
        return validateContentField(value || '', 'Arabic hero description', false, 0, 500);
      case 'hero_cta_text_en':
        return validateContentField(value || '', 'English CTA text', false, 0, 50);
      case 'hero_cta_text_ar':
        return validateContentField(value || '', 'Arabic CTA text', false, 0, 50);
      case 'hero_cta_link_en':
      case 'hero_cta_link_ar':
        return validateUrl(value || '');
      case 'hero_image_en':
      case 'hero_image_ar':
        return validateUrl(value || '');
      case 'hero_bg_color_primary_en':
      case 'hero_bg_color_primary_ar':
      case 'hero_bg_color_secondary_en':
      case 'hero_bg_color_secondary_ar':
      case 'hero_text_color_en':
      case 'hero_text_color_ar':
        return value && !/^#[0-9A-Fa-f]{6}$/.test(value) ? 'Please enter a valid hex color (e.g., #3B82F6)' : '';
      case 'hero_overlay_opacity_en':
      case 'hero_overlay_opacity_ar':
        const opacity = parseFloat(value);
        return (isNaN(opacity) || opacity < 0 || opacity > 1) ? 'Opacity must be between 0 and 1' : '';
      case 'meta_title_en':
        return validateSeoField(value || '', 'English meta title', 60);
      case 'meta_title_ar':
        return validateSeoField(value || '', 'Arabic meta title', 60);
      case 'meta_description_en':
        return validateSeoField(value || '', 'English meta description', 160);
      case 'meta_description_ar':
        return validateSeoField(value || '', 'Arabic meta description', 160);
      case 'og_title_en':
        return validateSeoField(value || '', 'English OG title', 60);
      case 'og_title_ar':
        return validateSeoField(value || '', 'Arabic OG title', 60);
      case 'og_description_en':
        return validateSeoField(value || '', 'English OG description', 200);
      case 'og_description_ar':
        return validateSeoField(value || '', 'Arabic OG description', 200);
      case 'content_summary_en':
        return validateContentField(value || '', 'English content summary', false, 0, 500);
      case 'content_summary_ar':
        return validateContentField(value || '', 'Arabic content summary', false, 0, 500);
      default:
        return '';
    }
  };

  const handleFieldChange = (fieldKey: string, value: any) => {
    // Update the appropriate state
    if (fieldKey === 'slug' || fieldKey === 'display_order' || fieldKey === 'status' ||
        fieldKey === 'menu_position' || fieldKey === 'country_id' ||
        fieldKey === 'is_featured') {
      setPageData(prev => ({ ...prev, [fieldKey]: value }));
    } else if (fieldKey.endsWith('_en')) {
      const key = fieldKey.replace('_en', '');
      setContentEn(prev => ({ ...prev, [key]: value }));
    } else if (fieldKey.endsWith('_ar')) {
      const key = fieldKey.replace('_ar', '');
      setContentAr(prev => ({ ...prev, [key]: value }));
    } else if (['hero_bg_color_primary', 'hero_bg_color_secondary', 'hero_gradient_direction',
               'hero_text_color', 'hero_overlay_opacity', 'hero_pattern', 'hero_animation_speed', 'hero_image'].includes(fieldKey)) {
      // These fields are shared between both languages
      setContentEn(prev => ({ ...prev, [fieldKey]: value }));
      setContentAr(prev => ({ ...prev, [fieldKey]: value }));
    }

    // Mark field as touched
    setTouched(prev => ({ ...prev, [fieldKey]: true }));

    // Validate field and update errors
    const error = validateField(fieldKey, value);
    setErrors(prev => ({
      ...prev,
      [fieldKey]: error
    }));
  };

  const validateAllFields = (): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Validate basic fields
    const basicFields = [
      { key: 'slug', value: pageData.slug },
      { key: 'display_order', value: pageData.display_order }
    ];

    basicFields.forEach(({ key, value }) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    // Validate all English content fields
    const contentFieldsEn = [
      { key: 'hero_title_en', value: contentEn.hero_title },
      { key: 'hero_subtitle_en', value: contentEn.hero_subtitle },
      { key: 'hero_description_en', value: contentEn.hero_description },
      { key: 'hero_cta_text_en', value: contentEn.hero_cta_text },
      { key: 'hero_cta_link_en', value: contentEn.hero_cta_link },
      { key: 'content_summary_en', value: contentEn.content_summary },
      { key: 'meta_title_en', value: contentEn.meta_title },
      { key: 'meta_description_en', value: contentEn.meta_description },
      { key: 'meta_keywords_en', value: contentEn.meta_keywords },
      { key: 'og_title_en', value: contentEn.og_title },
      { key: 'og_description_en', value: contentEn.og_description }
    ];

    contentFieldsEn.forEach(({ key, value }) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    // Validate all Arabic content fields
    const contentFieldsAr = [
      { key: 'hero_title_ar', value: contentAr.hero_title },
      { key: 'hero_subtitle_ar', value: contentAr.hero_subtitle },
      { key: 'hero_description_ar', value: contentAr.hero_description },
      { key: 'hero_cta_text_ar', value: contentAr.hero_cta_text },
      { key: 'hero_cta_link_ar', value: contentAr.hero_cta_link },
      { key: 'content_summary_ar', value: contentAr.content_summary },
      { key: 'meta_title_ar', value: contentAr.meta_title },
      { key: 'meta_description_ar', value: contentAr.meta_description },
      { key: 'meta_keywords_ar', value: contentAr.meta_keywords },
      { key: 'og_title_ar', value: contentAr.og_title },
      { key: 'og_description_ar', value: contentAr.og_description }
    ];

    contentFieldsAr.forEach(({ key, value }) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    // Validate sections
    const sectionErrors = validateSections();
    Object.entries(sectionErrors).forEach(([key, error]) => {
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    // Validate business logic
    const businessErrors = validateBusinessLogic();
    Object.entries(businessErrors).forEach(([key, error]) => {
      if (error) {
        newErrors[key] = error;
        // Some business logic errors are warnings, not blocking
        const warningKeys = ['content_consistency', 'slug_seo_length', 'main_menu_order', 'hero_not_first', 'cta_not_last'];
        if (!warningKeys.includes(key)) {
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return { isValid, errors: newErrors };
  };

  // Load existing page content for editing
  const loadExistingPageContent = async (pageId: number) => {
    try {
      const response = await adminApi.servicePages.getById(pageId) as {
        success: boolean;
        data: any;
      };

      if (response.success && response.data) {
        const pageData = response.data;

        // Load content if available
        if (pageData.content) {
          if (pageData.content.en) {
            const enContent = { ...pageData.content.en };
            // Ensure card_features is always an array
            if (typeof enContent.card_features === 'string') {
              try {
                enContent.card_features = JSON.parse(enContent.card_features);
              } catch {
                enContent.card_features = [];
              }
            } else if (!Array.isArray(enContent.card_features)) {
              enContent.card_features = ['Company Registration', 'Business License', 'Tax Registration', 'Bank Account Support'];
            }

            // Load card gradient colors (hex values)
            if (enContent.card_gradient_from && enContent.card_gradient_from.startsWith('#')) {
              setCardPrimaryColor(enContent.card_gradient_from);
            }
            if (enContent.card_gradient_to && enContent.card_gradient_to.startsWith('#')) {
              setCardSecondaryColor(enContent.card_gradient_to);
            }

            setContentEn(enContent);
          }
          if (pageData.content.ar) {
            const arContent = { ...pageData.content.ar };
            // Ensure card_features is always an array
            if (typeof arContent.card_features === 'string') {
              try {
                arContent.card_features = JSON.parse(arContent.card_features);
              } catch {
                arContent.card_features = [];
              }
            } else if (!Array.isArray(arContent.card_features)) {
              arContent.card_features = ['تسجيل الشركة', 'الترخيص التجاري', 'التسجيل الضريبي', 'دعم الحساب المصرفي'];
            }
            setContentAr(arContent);
          }
        }

        // Load sections if available
        if (pageData.sections) {
          setSections(pageData.sections);
        }
      }
    } catch (error) {    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoading(true);

    // Mark all fields as touched for validation display
    const allFields = [
      'slug', 'display_order', 'hero_title_en', 'hero_title_ar',
      'hero_subtitle_en', 'hero_subtitle_ar', 'hero_description_en', 'hero_description_ar',
      'hero_cta_text_en', 'hero_cta_text_ar', 'hero_cta_link_en', 'hero_cta_link_ar',
      'meta_title_en', 'meta_title_ar', 'meta_description_en', 'meta_description_ar',
      'meta_keywords_en', 'meta_keywords_ar', 'og_title_en', 'og_title_ar',
      'og_description_en', 'og_description_ar', 'content_summary_en', 'content_summary_ar'
    ];

    const touchedState: Record<string, boolean> = {};
    allFields.forEach(field => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    // Validate all fields
    const validationResult = validateAllFields();

    if (!validationResult.isValid) {
      setLoading(false);
      setIsSubmitting(false);

      // Get the validation errors to determine which tab to switch to
      const errorFields = Object.keys(validationResult.errors).filter(key => validationResult.errors[key]);

      // Priority order: basic -> content-en -> content-ar -> sections -> seo
      if (errorFields.some(field =>
        field === 'slug' || field === 'display_order' ||
        ['slug_seo_length', 'main_menu_order', 'featured_unpublished'].includes(field)
      )) {
        setActiveTab('basic');
      } else if (errorFields.some(field =>
        field.endsWith('_en') &&
        !field.includes('meta') &&
        !field.includes('og')
      ) || errorFields.includes('cta_missing_link') || errorFields.includes('cta_missing_text')) {
        setActiveTab('content-en');
      } else if (errorFields.some(field =>
        field.endsWith('_ar') &&
        !field.includes('meta') &&
        !field.includes('og')
      ) || errorFields.includes('content_consistency')) {
        setActiveTab('content-ar');
      } else if (errorFields.some(field =>
        field.startsWith('section_') || field === 'duplicate_keys' ||
        ['hero_not_first', 'cta_not_last', 'duplicate_hero', 'duplicate_packages', 'duplicate_cta'].includes(field)
      )) {
        setActiveTab('sections');
      } else if (errorFields.some(field =>
        field.includes('meta') ||
        field.includes('og')
      )) {
        setActiveTab('seo');
      }

      // Show a general error message      return;
    }

    try {
      const submitData = {
        ...pageData,
        content: {
          en: contentEn,
          ar: contentAr
        },
        sections: sections
      };

      await onSave(submitData);
      onClose();
    } catch (error) {    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const selectedCountry = countries.find(c => c.id === pageData.country_id);

  // Helper component for displaying field errors
  const FieldError: React.FC<{ fieldKey: string }> = ({ fieldKey }) => {
    const error = errors[fieldKey];
    const isFieldTouched = touched[fieldKey];
    if (!error || !isFieldTouched) return null;
    return (
      <p className="text-red-500 text-sm mt-1 flex items-center">
        <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        {error}
      </p>
    );
  };

  // Helper function to get input className with error state
  const getInputClassName = (fieldKey: string, baseClassName = '') => {
    const hasError = errors[fieldKey] && touched[fieldKey];
    const errorClasses = hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500';
    return `${baseClassName} ${errorClasses}`.trim();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">
              {page ? 'Edit Service Page' : 'Create New Service Page'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 flex-shrink-0">
            <nav className="flex space-x-8 px-6">
              {(() => {
                // Get US and SA country codes from countries prop
                const usCountry = countries?.find(c => c.code === 'US');
                const saCountry = countries?.find(c => c.code === 'SA');

                return [
                  { id: 'basic', label: 'Basic Info', icon: '⚙️', type: 'emoji' },
                  { id: 'content-en', label: 'English Content', icon: usCountry?.code || 'US', flagEmoji: usCountry?.flag_emoji || '🇺🇸', type: 'flag' },
                  { id: 'content-ar', label: 'Arabic Content', icon: saCountry?.code || 'SA', flagEmoji: saCountry?.flag_emoji || '🇸🇦', type: 'flag' },
                  { id: 'hero-design', label: 'Hero Design', icon: '🎨', type: 'emoji' },
                  { id: 'landing-card', label: 'Landing Card', icon: '🎴', type: 'emoji' },
                  { id: 'sections', label: 'Page Sections', icon: '📋', type: 'emoji' },
                  { id: 'seo', label: 'SEO & Meta', icon: '🔍', type: 'emoji' }
                ];
              })().map((tab) => {
                // Check if tab has errors
                const hasErrors = (() => {
                  const errorFields = Object.keys(errors).filter(key => errors[key] && touched[key]);
                  switch (tab.id) {
                    case 'basic':
                      return errorFields.some(field =>
                        field === 'slug' || field === 'display_order' ||
                        ['slug_seo_length', 'main_menu_order', 'featured_unpublished'].includes(field)
                      );
                    case 'content-en':
                      return errorFields.some(field =>
                        (field.endsWith('_en') && !field.includes('meta') && !field.includes('og')) ||
                        ['cta_missing_link', 'cta_missing_text'].includes(field)
                      );
                    case 'content-ar':
                      return errorFields.some(field =>
                        (field.endsWith('_ar') && !field.includes('meta') && !field.includes('og')) ||
                        field === 'content_consistency'
                      );
                    case 'hero-design':
                      return errorFields.some(field =>
                        field.includes('hero_bg_') || field.includes('hero_text_') ||
                        field.includes('hero_gradient_') || field.includes('hero_overlay_') ||
                        field.includes('hero_pattern') || field.includes('hero_image')
                      );
                    case 'sections':
                      return errorFields.some(field =>
                        field.startsWith('section_') || field === 'duplicate_keys' ||
                        ['hero_not_first', 'cta_not_last', 'duplicate_hero', 'duplicate_packages', 'duplicate_cta'].includes(field)
                      );
                    case 'seo':
                      return errorFields.some(field => field.includes('meta') || field.includes('og'));
                    default:
                      return false;
                  }
                })();

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 relative ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.type === 'flag' ? (
                      <img
                        src={getFlagUrl(tab.icon, '24x18')}
                        alt={`${tab.label} flag`}
                        className="w-6 h-4 object-cover rounded-sm"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentNode as HTMLElement;
                          if (parent && !parent.querySelector('.flag-fallback')) {
                            const fallbackSpan = document.createElement('span');
                            fallbackSpan.className = 'flag-fallback text-lg';
                            fallbackSpan.textContent = (tab as any).flagEmoji || '🌍';
                            parent.appendChild(fallbackSpan);
                          }
                        }}
                      />
                    ) : tab.type === 'image' ? (
                      <img
                        src={tab.icon}
                        alt={`${tab.label} flag`}
                        className="w-4 h-3 object-cover rounded-sm"
                      />
                    ) : (
                      <span className="text-lg leading-none">{tab.icon}</span>
                    )}
                    <span>{tab.label}</span>
                    {hasErrors && (
                      <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" title="This tab has validation errors"></span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-6 pb-8">
                  {/* Business Logic Errors/Warnings for Basic Tab */}
                  {Object.keys(errors).some(key =>
                    ['slug_seo_length', 'main_menu_order', 'featured_unpublished'].includes(key)
                  ) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-amber-800 mb-2">Configuration Issues:</h4>
                          <ul className="text-sm text-amber-700 space-y-1">
                            {Object.entries(errors)
                              .filter(([key]) => ['slug_seo_length', 'main_menu_order', 'featured_unpublished'].includes(key))
                              .map(([key, error]) => (
                                <li key={key} className="flex items-start">
                                  <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  <span>{error}</span>
                                </li>
                              ))
                            }
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Country Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <select
                        value={pageData.country_id || ''}
                        onChange={(e) => setPageData({
                          ...pageData,
                          country_id: e.target.value ? parseInt(e.target.value) : null
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select a country</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.flag_emoji} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Slug */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL Slug *
                      </label>
                      <input
                        type="text"
                        value={pageData.slug}
                        onChange={(e) => handleFieldChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        onBlur={() => setTouched(prev => ({ ...prev, slug: true }))}
                        className={getInputClassName('slug', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="e.g., saudi-arabia"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Only lowercase letters, numbers, and hyphens allowed</p>
                      <FieldError fieldKey="slug" />
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={pageData.status}
                        onChange={(e) => setPageData({ ...pageData, status: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>

                    {/* Display Order */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Display Order *
                      </label>
                      <input
                        type="number"
                        value={pageData.display_order}
                        onChange={(e) => handleFieldChange('display_order', parseInt(e.target.value) || 0)}
                        onBlur={() => setTouched(prev => ({ ...prev, display_order: true }))}
                        className={getInputClassName('display_order', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        min="0"
                        max="999"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in menus</p>
                      <FieldError fieldKey="display_order" />
                    </div>

                    {/* Menu Position */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Menu Position
                      </label>
                      <select
                        value={pageData.menu_position}
                        onChange={(e) => setPageData({ ...pageData, menu_position: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="main">Main Menu</option>
                        <option value="services">Services Menu</option>
                        <option value="footer">Footer</option>
                        <option value="none">None</option>
                      </select>
                    </div>

                  </div>

                  {/* Featured Checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={pageData.is_featured}
                      onChange={(e) => setPageData({ ...pageData, is_featured: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
                      Featured Page
                    </label>
                  </div>

                  {/* Preview */}
                  {selectedCountry && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Preview URL</h3>
                      <p className="text-sm text-gray-600">
                        https://patrixglobal.com/services/{pageData.slug}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedCountry.flag_emoji} {selectedCountry.name} • {pageData.status} • Order: {pageData.display_order}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* English Content Tab */}
              {activeTab === 'content-en' && (
                <div className="space-y-6 pb-8">
                  {/* CTA Consistency Errors */}
                  {(errors.cta_missing_link || errors.cta_missing_text) && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-orange-800 mb-2">CTA Configuration Issues:</h4>
                          <ul className="text-sm text-orange-700 space-y-1">
                            {errors.cta_missing_link && (
                              <li className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                <span>{errors.cta_missing_link}</span>
                              </li>
                            )}
                            {errors.cta_missing_text && (
                              <li className="flex items-start">
                                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                <span>{errors.cta_missing_text}</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hero Title (English) *
                      </label>
                      <input
                        type="text"
                        value={contentEn.hero_title}
                        onChange={(e) => handleFieldChange('hero_title_en', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_title_en: true }))}
                        className={getInputClassName('hero_title_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="Company Formation in..."
                        maxLength={150}
                        required
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Main page heading visible to users</span>
                        <span>{contentEn.hero_title.length}/150</span>
                      </div>
                      <FieldError fieldKey="hero_title_en" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hero Subtitle (English)
                      </label>
                      <input
                        type="text"
                        value={contentEn.hero_subtitle}
                        onChange={(e) => handleFieldChange('hero_subtitle_en', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_subtitle_en: true }))}
                        className={getInputClassName('hero_subtitle_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="Professional business setup services..."
                        maxLength={200}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Optional subtitle below the main title</span>
                        <span>{contentEn.hero_subtitle.length}/200</span>
                      </div>
                      <FieldError fieldKey="hero_subtitle_en" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hero Description (English)
                    </label>
                    <textarea
                      value={contentEn.hero_description}
                      onChange={(e) => handleFieldChange('hero_description_en', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, hero_description_en: true }))}
                      rows={4}
                      className={getInputClassName('hero_description_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                      placeholder="Establish your business with our expert guidance..."
                      maxLength={500}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Detailed description for the hero section</span>
                      <span>{contentEn.hero_description.length}/500</span>
                    </div>
                    <FieldError fieldKey="hero_description_en" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CTA Button Text (English)
                      </label>
                      <input
                        type="text"
                        value={contentEn.hero_cta_text}
                        onChange={(e) => handleFieldChange('hero_cta_text_en', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_cta_text_en: true }))}
                        className={getInputClassName('hero_cta_text_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="Get Started Today"
                        maxLength={50}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Text displayed on the call-to-action button</span>
                        <span>{contentEn.hero_cta_text.length}/50</span>
                      </div>
                      <FieldError fieldKey="hero_cta_text_en" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CTA Button Link
                      </label>
                      <input
                        type="text"
                        value={contentEn.hero_cta_link}
                        onChange={(e) => handleFieldChange('hero_cta_link_en', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_cta_link_en: true }))}
                        className={getInputClassName('hero_cta_link_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="/register"
                      />
                      <p className="text-xs text-gray-500 mt-1">URL where the button should redirect (e.g., /register, https://example.com)</p>
                      <FieldError fieldKey="hero_cta_link_en" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content Summary (English)
                    </label>
                    <textarea
                      value={contentEn.content_summary}
                      onChange={(e) => handleFieldChange('content_summary_en', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, content_summary_en: true }))}
                      rows={3}
                      className={getInputClassName('content_summary_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                      placeholder="Complete company formation services including..."
                      maxLength={500}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Brief summary of the page content and services</span>
                      <span>{contentEn.content_summary.length}/500</span>
                    </div>
                    <FieldError fieldKey="content_summary_en" />
                  </div>
                </div>
              )}

              {/* Arabic Content Tab */}
              {activeTab === 'content-ar' && (
                <div className="space-y-6 pb-8" dir="rtl">
                  {/* Content Consistency Warnings */}
                  {errors.content_consistency && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">محتوى غير متسق بين اللغات:</h4>
                          <p className="text-sm text-blue-700">{errors.content_consistency}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        عنوان البطل (عربي) *
                      </label>
                      <input
                        type="text"
                        value={contentAr.hero_title}
                        onChange={(e) => handleFieldChange('hero_title_ar', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_title_ar: true }))}
                        className={getInputClassName('hero_title_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="تأسيس الشركات في..."
                        maxLength={150}
                        required
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{contentAr.hero_title.length}/150</span>
                        <span>العنوان الرئيسي للصفحة المرئي للمستخدمين</span>
                      </div>
                      <FieldError fieldKey="hero_title_ar" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        العنوان الفرعي (عربي)
                      </label>
                      <input
                        type="text"
                        value={contentAr.hero_subtitle}
                        onChange={(e) => handleFieldChange('hero_subtitle_ar', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_subtitle_ar: true }))}
                        className={getInputClassName('hero_subtitle_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="خدمات تأسيس الأعمال المهنية..."
                        maxLength={200}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{contentAr.hero_subtitle.length}/200</span>
                        <span>العنوان الفرعي الاختياري أسفل العنوان الرئيسي</span>
                      </div>
                      <FieldError fieldKey="hero_subtitle_ar" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      وصف البطل (عربي)
                    </label>
                    <textarea
                      value={contentAr.hero_description}
                      onChange={(e) => handleFieldChange('hero_description_ar', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, hero_description_ar: true }))}
                      rows={4}
                      className={getInputClassName('hero_description_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                      placeholder="أسس عملك مع إرشادنا المتخصص..."
                      maxLength={500}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{contentAr.hero_description.length}/500</span>
                      <span>وصف مفصل لقسم البطل</span>
                    </div>
                    <FieldError fieldKey="hero_description_ar" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        نص الزر (عربي)
                      </label>
                      <input
                        type="text"
                        value={contentAr.hero_cta_text}
                        onChange={(e) => handleFieldChange('hero_cta_text_ar', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_cta_text_ar: true }))}
                        className={getInputClassName('hero_cta_text_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="ابدأ اليوم"
                        maxLength={50}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{contentAr.hero_cta_text.length}/50</span>
                        <span>النص المعروض على زر الدعوة للعمل</span>
                      </div>
                      <FieldError fieldKey="hero_cta_text_ar" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        رابط الزر
                      </label>
                      <input
                        type="text"
                        value={contentAr.hero_cta_link}
                        onChange={(e) => handleFieldChange('hero_cta_link_ar', e.target.value)}
                        onBlur={() => setTouched(prev => ({ ...prev, hero_cta_link_ar: true }))}
                        className={getInputClassName('hero_cta_link_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                        placeholder="/register"
                      />
                      <p className="text-xs text-gray-500 mt-1">رابط حيث يجب أن يعيد توجيه الزر (مثل /register أو https://example.com)</p>
                      <FieldError fieldKey="hero_cta_link_ar" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ملخص المحتوى (عربي)
                    </label>
                    <textarea
                      value={contentAr.content_summary}
                      onChange={(e) => handleFieldChange('content_summary_ar', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, content_summary_ar: true }))}
                      rows={3}
                      className={getInputClassName('content_summary_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                      placeholder="خدمات تأسيس شركات كاملة تشمل..."
                      maxLength={500}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{contentAr.content_summary.length}/500</span>
                      <span>ملخص موجز لمحتوى الصفحة والخدمات</span>
                    </div>
                    <FieldError fieldKey="content_summary_ar" />
                  </div>
                </div>
              )}

              {/* Hero Design Tab */}
              {activeTab === 'hero-design' && (
                <div className="space-y-6 pb-8">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-white text-lg">🎨</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Hero Section Design</h3>
                        <p className="text-sm text-gray-600">Customize the visual appearance of your hero section</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Background Colors */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">🌈 Background Colors</h4>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Primary Color
                          </label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={contentEn.hero_bg_color_primary}
                              onChange={(e) => {
                                setContentEn(prev => ({ ...prev, hero_bg_color_primary: e.target.value }));
                                setContentAr(prev => ({ ...prev, hero_bg_color_primary: e.target.value }));
                              }}
                              className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={contentEn.hero_bg_color_primary}
                              onChange={(e) => {
                                setContentEn(prev => ({ ...prev, hero_bg_color_primary: e.target.value }));
                                setContentAr(prev => ({ ...prev, hero_bg_color_primary: e.target.value }));
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="#3B82F6"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Secondary Color
                          </label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={contentEn.hero_bg_color_secondary}
                              onChange={(e) => {
                                setContentEn(prev => ({ ...prev, hero_bg_color_secondary: e.target.value }));
                                setContentAr(prev => ({ ...prev, hero_bg_color_secondary: e.target.value }));
                              }}
                              className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={contentEn.hero_bg_color_secondary}
                              onChange={(e) => {
                                setContentEn(prev => ({ ...prev, hero_bg_color_secondary: e.target.value }));
                                setContentAr(prev => ({ ...prev, hero_bg_color_secondary: e.target.value }));
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="#1E40AF"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Text Color
                          </label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={contentEn.hero_text_color}
                              onChange={(e) => {
                                setContentEn(prev => ({ ...prev, hero_text_color: e.target.value }));
                                setContentAr(prev => ({ ...prev, hero_text_color: e.target.value }));
                              }}
                              className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={contentEn.hero_text_color}
                              onChange={(e) => {
                                setContentEn(prev => ({ ...prev, hero_text_color: e.target.value }));
                                setContentAr(prev => ({ ...prev, hero_text_color: e.target.value }));
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="#FFFFFF"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Layout & Effects */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">⚡ Layout & Effects</h4>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Gradient Direction
                          </label>
                          <select
                            value={contentEn.hero_gradient_direction}
                            onChange={(e) => {
                              setContentEn(prev => ({ ...prev, hero_gradient_direction: e.target.value }));
                              setContentAr(prev => ({ ...prev, hero_gradient_direction: e.target.value }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="to-r">Left to Right</option>
                            <option value="to-l">Right to Left</option>
                            <option value="to-b">Top to Bottom</option>
                            <option value="to-t">Bottom to Top</option>
                            <option value="to-br">Top-Left to Bottom-Right</option>
                            <option value="to-bl">Top-Right to Bottom-Left</option>
                            <option value="to-tr">Bottom-Left to Top-Right</option>
                            <option value="to-tl">Bottom-Right to Top-Left</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Background Pattern
                          </label>
                          <select
                            value={contentEn.hero_pattern}
                            onChange={(e) => {
                              setContentEn(prev => ({ ...prev, hero_pattern: e.target.value }));
                              setContentAr(prev => ({ ...prev, hero_pattern: e.target.value }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="none">No Pattern</option>
                            <option value="animated-circles">Animated Circles</option>
                            <option value="floating-shapes">Floating Geometric Shapes</option>
                            <option value="subtle-dots">Subtle Dots Pattern</option>
                            <option value="grid-lines">Grid Lines</option>
                            <option value="diagonal-lines">Diagonal Lines</option>
                            <option value="hexagon-pattern">Hexagon Pattern</option>
                            <option value="wave-pattern">Wave Pattern</option>
                            <option value="circuit-pattern">Circuit Pattern</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Patterns add visual interest without overwhelming content
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Animation Speed
                          </label>
                          <select
                            value={contentEn.hero_animation_speed}
                            onChange={(e) => {
                              setContentEn(prev => ({ ...prev, hero_animation_speed: e.target.value }));
                              setContentAr(prev => ({ ...prev, hero_animation_speed: e.target.value }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="slow">Slow (Relaxed)</option>
                            <option value="normal">Normal</option>
                            <option value="fast">Fast (Energetic)</option>
                            <option value="very-fast">Very Fast</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Controls the speed of animated patterns
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Overlay Opacity: {contentEn.hero_overlay_opacity}
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={contentEn.hero_overlay_opacity}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              setContentEn(prev => ({ ...prev, hero_overlay_opacity: value }));
                              setContentAr(prev => ({ ...prev, hero_overlay_opacity: value }));
                            }}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Transparent</span>
                            <span>Opaque</span>
                          </div>
                        </div>
                      </div>

                      {/* Background Image */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">🖼️ Background Image</h4>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Image URL
                          </label>
                          <input
                            type="url"
                            value={contentEn.hero_image}
                            onChange={(e) => {
                              setContentEn(prev => ({ ...prev, hero_image: e.target.value }));
                              setContentAr(prev => ({ ...prev, hero_image: e.target.value }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="https://example.com/hero-image.jpg"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Leave empty to use gradient background only
                          </p>
                        </div>

                        {contentEn.hero_image && (
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Image Preview
                            </label>
                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                              <img
                                src={contentEn.hero_image}
                                alt="Hero background preview"
                                className="w-full h-32 object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Live Preview */}
                    <div className="mt-8 border-t border-gray-200 pt-6">
                      <h4 className="font-medium text-gray-900 mb-4">🔍 Live Preview</h4>
                      <div
                        className={`relative rounded-lg p-8 min-h-[200px] flex items-center justify-center bg-gradient-${contentEn.hero_gradient_direction}`}
                        style={{
                          background: contentEn.hero_image
                            ? `linear-gradient(${contentEn.hero_gradient_direction === 'to-r' ? 'to right' :
                                contentEn.hero_gradient_direction === 'to-l' ? 'to left' :
                                contentEn.hero_gradient_direction === 'to-b' ? 'to bottom' :
                                contentEn.hero_gradient_direction === 'to-t' ? 'to top' :
                                contentEn.hero_gradient_direction === 'to-br' ? 'to bottom right' :
                                contentEn.hero_gradient_direction === 'to-bl' ? 'to bottom left' :
                                contentEn.hero_gradient_direction === 'to-tr' ? 'to top right' :
                                'to top left'},
                                ${contentEn.hero_bg_color_primary}${Math.round(contentEn.hero_overlay_opacity * 255).toString(16).padStart(2, '0')},
                                ${contentEn.hero_bg_color_secondary}${Math.round(contentEn.hero_overlay_opacity * 255).toString(16).padStart(2, '0')}),
                                url('${contentEn.hero_image}')`
                            : `linear-gradient(${contentEn.hero_gradient_direction === 'to-r' ? 'to right' :
                                contentEn.hero_gradient_direction === 'to-l' ? 'to left' :
                                contentEn.hero_gradient_direction === 'to-b' ? 'to bottom' :
                                contentEn.hero_gradient_direction === 'to-t' ? 'to top' :
                                contentEn.hero_gradient_direction === 'to-br' ? 'to bottom right' :
                                contentEn.hero_gradient_direction === 'to-bl' ? 'to bottom left' :
                                contentEn.hero_gradient_direction === 'to-tr' ? 'to top right' :
                                'to top left'},
                                ${contentEn.hero_bg_color_primary}${Math.round(contentEn.hero_overlay_opacity * 255).toString(16).padStart(2, '0')},
                                ${contentEn.hero_bg_color_secondary}${Math.round(contentEn.hero_overlay_opacity * 255).toString(16).padStart(2, '0')})`,
                          backgroundSize: contentEn.hero_image ? 'cover' : 'auto',
                          backgroundPosition: 'center',
                          color: contentEn.hero_text_color
                        }}
                      >
                        {/* Pattern Overlay */}
                        {contentEn.hero_pattern !== 'none' && (
                          <>
                            {/* Animated Circles */}
                            {contentEn.hero_pattern === 'animated-circles' && (
                              <>
                                <div
                                  className="absolute top-4 right-4 w-32 h-32 rounded-full opacity-10 animate-pulse"
                                  style={{
                                    backgroundColor: contentEn.hero_text_color,
                                    animationDuration: contentEn.hero_animation_speed === 'slow' ? '3s' :
                                      contentEn.hero_animation_speed === 'fast' ? '1s' :
                                      contentEn.hero_animation_speed === 'very-fast' ? '0.5s' : '2s'
                                  }}
                                ></div>
                                <div
                                  className="absolute bottom-4 left-4 w-24 h-24 rounded-full opacity-10 animate-pulse"
                                  style={{
                                    backgroundColor: contentEn.hero_text_color,
                                    animationDelay: '1s',
                                    animationDuration: contentEn.hero_animation_speed === 'slow' ? '3s' :
                                      contentEn.hero_animation_speed === 'fast' ? '1s' :
                                      contentEn.hero_animation_speed === 'very-fast' ? '0.5s' : '2s'
                                  }}
                                ></div>
                                <div
                                  className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full opacity-5 animate-pulse"
                                  style={{
                                    backgroundColor: contentEn.hero_text_color,
                                    animationDelay: '2s',
                                    animationDuration: contentEn.hero_animation_speed === 'slow' ? '3s' :
                                      contentEn.hero_animation_speed === 'fast' ? '1s' :
                                      contentEn.hero_animation_speed === 'very-fast' ? '0.5s' : '2s'
                                  }}
                                ></div>
                              </>
                            )}

                            {/* Floating Geometric Shapes */}
                            {contentEn.hero_pattern === 'floating-shapes' && (
                              <>
                                <div
                                  className="absolute top-8 right-8 w-8 h-8 opacity-10 animate-bounce"
                                  style={{
                                    backgroundColor: contentEn.hero_text_color,
                                    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                                    animationDuration: contentEn.hero_animation_speed === 'slow' ? '2s' :
                                      contentEn.hero_animation_speed === 'fast' ? '0.5s' :
                                      contentEn.hero_animation_speed === 'very-fast' ? '0.3s' : '1s'
                                  }}
                                ></div>
                                <div
                                  className="absolute bottom-8 left-8 w-6 h-6 opacity-10 animate-pulse"
                                  style={{
                                    backgroundColor: contentEn.hero_text_color,
                                    transform: 'rotate(45deg)',
                                    animationDelay: '0.5s',
                                    animationDuration: contentEn.hero_animation_speed === 'slow' ? '3s' :
                                      contentEn.hero_animation_speed === 'fast' ? '1s' :
                                      contentEn.hero_animation_speed === 'very-fast' ? '0.5s' : '2s'
                                  }}
                                ></div>
                                <div
                                  className="absolute top-1/3 right-1/4 w-4 h-4 rounded-full opacity-10 animate-ping"
                                  style={{
                                    backgroundColor: contentEn.hero_text_color,
                                    animationDelay: '1.5s',
                                    animationDuration: contentEn.hero_animation_speed === 'slow' ? '2s' :
                                      contentEn.hero_animation_speed === 'fast' ? '0.5s' :
                                      contentEn.hero_animation_speed === 'very-fast' ? '0.3s' : '1s'
                                  }}
                                ></div>
                              </>
                            )}

                            {/* Static Patterns */}
                            {!['animated-circles', 'floating-shapes'].includes(contentEn.hero_pattern) && (
                              <div
                                className="absolute inset-0 opacity-10"
                                style={{
                                  backgroundImage: contentEn.hero_pattern === 'subtle-dots' ?
                                    'radial-gradient(circle, currentColor 0.5px, transparent 0.5px)' :
                                    contentEn.hero_pattern === 'grid-lines' ?
                                    'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)' :
                                    contentEn.hero_pattern === 'diagonal-lines' ?
                                    'repeating-linear-gradient(45deg, currentColor, currentColor 1px, transparent 1px, transparent 15px)' :
                                    contentEn.hero_pattern === 'hexagon-pattern' ?
                                    'radial-gradient(circle at 50% 50%, currentColor 1px, transparent 1px), radial-gradient(circle at 25% 75%, currentColor 1px, transparent 1px)' :
                                    contentEn.hero_pattern === 'wave-pattern' ?
                                    'repeating-linear-gradient(0deg, transparent, transparent 10px, currentColor 10px, currentColor 11px)' :
                                    contentEn.hero_pattern === 'circuit-pattern' ?
                                    'linear-gradient(90deg, currentColor 1px, transparent 1px), linear-gradient(currentColor 1px, transparent 1px)' :
                                    'none',
                                  backgroundSize: contentEn.hero_pattern === 'subtle-dots' ? '15px 15px' :
                                    contentEn.hero_pattern === 'grid-lines' ? '25px 25px' :
                                    contentEn.hero_pattern === 'diagonal-lines' ? '20px 20px' :
                                    contentEn.hero_pattern === 'hexagon-pattern' ? '30px 30px, 30px 30px' :
                                    contentEn.hero_pattern === 'wave-pattern' ? '100% 20px' :
                                    contentEn.hero_pattern === 'circuit-pattern' ? '20px 20px' :
                                    '20px 20px',
                                  backgroundPosition: contentEn.hero_pattern === 'hexagon-pattern' ? '0 0, 15px 15px' : '0 0'
                                }}
                              />
                            )}
                          </>
                        )}

                        <div className="relative z-10 text-center">
                          <h1 className="text-3xl font-bold mb-2">
                            {contentEn.hero_title || 'Hero Title Preview'}
                          </h1>
                          <p className="text-lg opacity-90 mb-4">
                            {contentEn.hero_subtitle || 'Hero subtitle preview text'}
                          </p>
                          <button
                            className="px-6 py-3 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200"
                          >
                            {contentEn.hero_cta_text || 'CTA Button'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Landing Card Tab */}
              {activeTab === 'landing-card' && (
                <div className="space-y-6 pb-8">
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-6 border border-green-200">
                    <div className="flex items-center mb-4">
                      <span className="text-3xl mr-3">🎴</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Landing Page Card Customization</h3>
                        <p className="text-sm text-gray-600">Control how this service appears on the home page (gradient applies to both languages)</p>
                      </div>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Card Preview</h4>
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-md">
                      {/* Card Header - matches ServicesSummary.tsx */}
                      <div
                        className="p-6 text-white relative overflow-hidden"
                        style={{
                          background: `linear-gradient(to right, ${cardPrimaryColor}, ${cardSecondaryColor})`
                        }}
                      >
                        <div className="absolute inset-0 bg-black opacity-10"></div>
                        <div className="flex items-center space-x-4 rtl:space-x-reverse relative z-10">
                          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-2 border-white/30">
                            {pageData.country_id && countries.find(c => c.id === pageData.country_id) ? (
                              <img
                                src={getFlagUrl(countries.find(c => c.id === pageData.country_id)?.code || '')}
                                alt={`${countries.find(c => c.id === pageData.country_id)?.name} flag`}
                                className="w-12 h-8 object-cover rounded"
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  target.style.display = 'none';
                                  const parent = target.parentNode as HTMLElement;
                                  if (parent && !parent.querySelector('.flag-fallback')) {
                                    const span = document.createElement('span');
                                    span.className = 'flag-fallback text-2xl';
                                    span.textContent = countries.find(c => c.id === pageData.country_id)?.flag_emoji || '🌍';
                                    parent.appendChild(span);
                                  }
                                }}
                              />
                            ) : (
                              <span className="text-3xl">🌍</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold mb-1">
                              {cardLanguage === 'en'
                                ? (contentEn.hero_title || 'Service Title')
                                : (contentAr.hero_title || 'عنوان الخدمة')}
                            </h3>
                          </div>
                        </div>
                      </div>

                      {/* Card Content - matches ServicesSummary.tsx */}
                      <div className="p-6">
                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {cardLanguage === 'en'
                            ? (contentEn.card_description || contentEn.hero_subtitle || 'Service description...')
                            : (contentAr.card_description || contentAr.hero_subtitle || 'وصف الخدمة...')}
                        </p>

                        {/* Features List */}
                        {cardLanguage === 'en' && Array.isArray(contentEn.card_features) && contentEn.card_features.length > 0 && (
                          <div className="space-y-3 mb-8">
                            {contentEn.card_features.map((feature, idx) => (
                              <div key={idx} className="flex items-center space-x-3">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    background: `linear-gradient(to right, ${cardPrimaryColor}, ${cardSecondaryColor})`
                                  }}
                                />
                                <span className="text-gray-700 font-medium">{feature}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {cardLanguage === 'ar' && Array.isArray(contentAr.card_features) && contentAr.card_features.length > 0 && (
                          <div className="space-y-3 mb-8" dir="rtl">
                            {contentAr.card_features.map((feature, idx) => (
                              <div key={idx} className="flex items-center space-x-reverse space-x-3">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    background: `linear-gradient(to right, ${cardPrimaryColor}, ${cardSecondaryColor})`
                                  }}
                                />
                                <span className="text-gray-700 font-medium">{feature}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Learn More Button */}
                        <button
                          className="w-full py-3 px-6 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 relative overflow-hidden"
                          style={{
                            background: `linear-gradient(to right, ${cardPrimaryColor}, ${cardSecondaryColor})`
                          }}
                        >
                          <span className="relative z-10">
                            {cardLanguage === 'en' ? 'Learn More →' : 'اعرف المزيد ←'}
                          </span>
                        </button>
                      </div>
                    </div>
                    {/* Language Toggle for Preview */}
                    <div className="flex justify-center mt-4 space-x-2">
                      <button
                        type="button"
                        onClick={() => setCardLanguage('en')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
                          cardLanguage === 'en'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <img src="https://flagcdn.com/w20/us.png" alt="US flag" className="w-5 h-4" />
                        <span>Preview English</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCardLanguage('ar')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
                          cardLanguage === 'ar'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <img src="https://flagcdn.com/w20/sa.png" alt="Saudi Arabia flag" className="w-5 h-4" />
                        <span>Preview Arabic</span>
                      </button>
                    </div>
                  </div>

                  {/* Gradient Picker Section - Shared for Both Languages */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Card Gradient (Applies to Both Languages)</h4>

                    {/* Custom RGB Color Pickers */}
                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Custom Colors</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Primary Color */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Primary Color (Start)
                          </label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={cardPrimaryColor}
                              onChange={(e) => {
                                setCardPrimaryColor(e.target.value);
                                setContentEn({ ...contentEn, card_gradient_from: e.target.value, card_gradient_to: cardSecondaryColor });
                                setContentAr({ ...contentAr, card_gradient_from: e.target.value, card_gradient_to: cardSecondaryColor });
                              }}
                              className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={cardPrimaryColor}
                              onChange={(e) => {
                                setCardPrimaryColor(e.target.value);
                                setContentEn({ ...contentEn, card_gradient_from: e.target.value, card_gradient_to: cardSecondaryColor });
                                setContentAr({ ...contentAr, card_gradient_from: e.target.value, card_gradient_to: cardSecondaryColor });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                              placeholder="#3B82F6"
                            />
                          </div>
                        </div>

                        {/* Secondary Color */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Secondary Color (End)
                          </label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="color"
                              value={cardSecondaryColor}
                              onChange={(e) => {
                                setCardSecondaryColor(e.target.value);
                                setContentEn({ ...contentEn, card_gradient_from: cardPrimaryColor, card_gradient_to: e.target.value });
                                setContentAr({ ...contentAr, card_gradient_from: cardPrimaryColor, card_gradient_to: e.target.value });
                              }}
                              className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={cardSecondaryColor}
                              onChange={(e) => {
                                setCardSecondaryColor(e.target.value);
                                setContentEn({ ...contentEn, card_gradient_from: cardPrimaryColor, card_gradient_to: e.target.value });
                                setContentAr({ ...contentAr, card_gradient_from: cardPrimaryColor, card_gradient_to: e.target.value });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                              placeholder="#0EA5E9"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Preset Gradients */}
                    <div className="border-t pt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Quick Presets</h5>
                      <p className="text-xs text-gray-600 mb-3">Click on a gradient to apply it</p>

                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { color1: '#F59E0B', color2: '#EA580C', label: 'Amber to Orange' },
                          { color1: '#10B981', color2: '#059669', label: 'Green to Emerald' },
                          { color1: '#EF4444', color2: '#3B82F6', label: 'Red to Blue' },
                          { color1: '#A855F7', color2: '#EC4899', label: 'Purple to Pink' },
                          { color1: '#3B82F6', color2: '#06B6D4', label: 'Blue to Cyan' },
                          { color1: '#EC4899', color2: '#F43F5E', label: 'Pink to Rose' },
                          { color1: '#6366F1', color2: '#A855F7', label: 'Indigo to Purple' },
                          { color1: '#14B8A6', color2: '#06B6D4', label: 'Teal to Cyan' },
                          { color1: '#F97316', color2: '#DC2626', label: 'Orange to Red' },
                          { color1: '#EAB308', color2: '#F59E0B', label: 'Yellow to Orange' },
                          { color1: '#84CC16', color2: '#16A34A', label: 'Lime to Green' },
                          { color1: '#0EA5E9', color2: '#2563EB', label: 'Sky to Blue' },
                        ].map((gradient, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setCardPrimaryColor(gradient.color1);
                              setCardSecondaryColor(gradient.color2);
                              setContentEn({ ...contentEn, card_gradient_from: gradient.color1, card_gradient_to: gradient.color2 });
                              setContentAr({ ...contentAr, card_gradient_from: gradient.color1, card_gradient_to: gradient.color2 });
                            }}
                            className="h-16 rounded-lg hover:scale-105 transition-transform border-2 border-gray-200 hover:border-gray-400"
                            style={{
                              background: `linear-gradient(to right, ${gradient.color1}, ${gradient.color2})`
                            }}
                            title={gradient.label}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* English Content */}
                    <div className="space-y-6">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                        <img src="https://flagcdn.com/w20/us.png" alt="US flag" className="w-5 h-4" />
                        <span>English Card Content</span>
                      </h4>

                      {/* Card Description Override */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Description (Optional)
                        </label>
                        <textarea
                          value={contentEn.card_description}
                          onChange={(e) => setContentEn({ ...contentEn, card_description: e.target.value })}
                          placeholder="Leave empty to use hero subtitle..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">If empty, hero subtitle will be used</p>
                      </div>

                      {/* Features List */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Features
                        </label>
                        <div className="space-y-2">
                          {Array.isArray(contentEn.card_features) && contentEn.card_features.map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={feature}
                                onChange={(e) => {
                                  const newFeatures = [...contentEn.card_features];
                                  newFeatures[idx] = e.target.value;
                                  setContentEn({ ...contentEn, card_features: newFeatures });
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                placeholder={`Feature ${idx + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newFeatures = contentEn.card_features.filter((_, i) => i !== idx);
                                  setContentEn({ ...contentEn, card_features: newFeatures });
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                                title="Remove"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          {(!Array.isArray(contentEn.card_features) || contentEn.card_features.length < 6) && (
                            <button
                              type="button"
                              onClick={() => {
                                const currentFeatures = Array.isArray(contentEn.card_features) ? contentEn.card_features : [];
                                setContentEn({ ...contentEn, card_features: [...currentFeatures, ''] });
                              }}
                              className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-primary-500 hover:text-primary-600"
                            >
                              + Add Feature
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arabic Content */}
                    <div className="space-y-6" dir="rtl">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center space-x-reverse space-x-2">
                        <img src="https://flagcdn.com/w20/sa.png" alt="Saudi Arabia flag" className="w-5 h-4" />
                        <span>محتوى البطاقة بالعربية</span>
                      </h4>

                      {/* Card Description Override */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          وصف البطاقة (اختياري)
                        </label>
                        <textarea
                          value={contentAr.card_description}
                          onChange={(e) => setContentAr({ ...contentAr, card_description: e.target.value })}
                          placeholder="اتركه فارغًا لاستخدام العنوان الفرعي..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">إذا كان فارغًا، سيتم استخدام العنوان الفرعي</p>
                      </div>

                      {/* Features List */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ميزات البطاقة
                        </label>
                        <div className="space-y-2">
                          {Array.isArray(contentAr.card_features) && contentAr.card_features.map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-reverse space-x-2">
                              <input
                                type="text"
                                value={feature}
                                onChange={(e) => {
                                  const newFeatures = [...contentAr.card_features];
                                  newFeatures[idx] = e.target.value;
                                  setContentAr({ ...contentAr, card_features: newFeatures });
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                placeholder={`ميزة ${idx + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newFeatures = contentAr.card_features.filter((_, i) => i !== idx);
                                  setContentAr({ ...contentAr, card_features: newFeatures });
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                                title="حذف"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          {(!Array.isArray(contentAr.card_features) || contentAr.card_features.length < 6) && (
                            <button
                              type="button"
                              onClick={() => {
                                const currentFeatures = Array.isArray(contentAr.card_features) ? contentAr.card_features : [];
                                setContentAr({ ...contentAr, card_features: [...currentFeatures, ''] });
                              }}
                              className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-primary-500 hover:text-primary-600"
                            >
                              + إضافة ميزة
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sections Tab */}
              {activeTab === 'sections' && (
                <div className="space-y-6 pb-8">
                  {/* Section Validation Errors */}
                  {Object.keys(errors).some(key => key.startsWith('section_') || key === 'duplicate_keys') && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-red-800 mb-2">Section Validation Issues:</h4>
                          <ul className="text-sm text-red-700 space-y-1">
                            {Object.entries(errors)
                              .filter(([key]) => key.startsWith('section_') || key === 'duplicate_keys')
                              .map(([key, error]) => (
                                <li key={key} className="flex items-start">
                                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  <span>{error}</span>
                                </li>
                              ))
                            }
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section Business Logic Warnings */}
                  {Object.keys(errors).some(key =>
                    ['hero_not_first', 'cta_not_last', 'duplicate_hero', 'duplicate_packages', 'duplicate_cta'].includes(key)
                  ) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-yellow-800 mb-2">Section Recommendations:</h4>
                          <ul className="text-sm text-yellow-700 space-y-1">
                            {Object.entries(errors)
                              .filter(([key]) => ['hero_not_first', 'cta_not_last', 'duplicate_hero', 'duplicate_packages', 'duplicate_cta'].includes(key))
                              .map(([key, error]) => (
                                <li key={key} className="flex items-start">
                                  <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  <span>{error}</span>
                                </li>
                              ))
                            }
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <SectionManager
                    sections={sections}
                    onSectionsChange={setSections}
                    language={sectionLanguage}
                    onLanguageChange={setSectionLanguage}
                  />
                </div>
              )}

              {/* SEO & Meta Tab */}
              {activeTab === 'seo' && (
                <div className="space-y-6 pb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* English SEO */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                        <img
                          src={getFlagUrl('US', '24x18')}
                          alt="US flag"
                          className="w-6 h-4 object-cover rounded-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentNode as HTMLElement;
                            if (parent && !parent.querySelector('.flag-fallback')) {
                              const span = document.createElement('span');
                              span.className = 'flag-fallback text-lg';
                              span.textContent = '🇺🇸';
                              parent.insertBefore(span, parent.children[1]);
                            }
                          }}
                        />
                        English SEO
                      </h3>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meta Title (English)
                        </label>
                        <input
                          type="text"
                          value={contentEn.meta_title}
                          onChange={(e) => handleFieldChange('meta_title_en', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, meta_title_en: true }))}
                          className={getInputClassName('meta_title_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="Company Formation in Country | Patrix Global"
                          maxLength={90}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Recommended: 50-60 characters</span>
                          <span className={contentEn.meta_title.length > 60 ? 'text-orange-500' : ''}>{contentEn.meta_title.length}/90</span>
                        </div>
                        <FieldError fieldKey="meta_title_en" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meta Description (English)
                        </label>
                        <textarea
                          value={contentEn.meta_description}
                          onChange={(e) => handleFieldChange('meta_description_en', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, meta_description_en: true }))}
                          rows={3}
                          className={getInputClassName('meta_description_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="Professional company formation services..."
                          maxLength={250}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Recommended: 150-160 characters</span>
                          <span className={contentEn.meta_description.length > 160 ? 'text-orange-500' : ''}>{contentEn.meta_description.length}/250</span>
                        </div>
                        <FieldError fieldKey="meta_description_en" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Keywords (English)
                        </label>
                        <input
                          type="text"
                          value={contentEn.meta_keywords}
                          onChange={(e) => handleFieldChange('meta_keywords_en', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, meta_keywords_en: true }))}
                          className={getInputClassName('meta_keywords_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="company formation, business setup, incorporation"
                          maxLength={255}
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate keywords with commas</p>
                        <FieldError fieldKey="meta_keywords_en" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          OG Title (English)
                        </label>
                        <input
                          type="text"
                          value={contentEn.og_title}
                          onChange={(e) => handleFieldChange('og_title_en', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, og_title_en: true }))}
                          className={getInputClassName('og_title_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="Company Formation in Country - Patrix Global"
                          maxLength={90}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Used when sharing on social media</span>
                          <span className={contentEn.og_title.length > 60 ? 'text-orange-500' : ''}>{contentEn.og_title.length}/90</span>
                        </div>
                        <FieldError fieldKey="og_title_en" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          OG Description (English)
                        </label>
                        <textarea
                          value={contentEn.og_description}
                          onChange={(e) => handleFieldChange('og_description_en', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, og_description_en: true }))}
                          rows={2}
                          className={getInputClassName('og_description_en', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="Establish your business with professional guidance..."
                          maxLength={300}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Recommended: up to 200 characters</span>
                          <span className={contentEn.og_description.length > 200 ? 'text-orange-500' : ''}>{contentEn.og_description.length}/300</span>
                        </div>
                        <FieldError fieldKey="og_description_en" />
                      </div>
                    </div>

                    {/* Arabic SEO */}
                    <div className="space-y-4" dir="rtl">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                        <img
                          src={getFlagUrl('SA', '24x18')}
                          alt="Saudi Arabia flag"
                          className="w-6 h-4 object-cover rounded-sm"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentNode as HTMLElement;
                            if (parent && !parent.querySelector('.flag-fallback')) {
                              const span = document.createElement('span');
                              span.className = 'flag-fallback text-lg';
                              span.textContent = '🇸🇦';
                              parent.insertBefore(span, parent.children[1]);
                            }
                          }}
                        />
                        Arabic SEO
                      </h3>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          عنوان ميتا (عربي)
                        </label>
                        <input
                          type="text"
                          value={contentAr.meta_title}
                          onChange={(e) => handleFieldChange('meta_title_ar', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, meta_title_ar: true }))}
                          className={getInputClassName('meta_title_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="تأسيس الشركات في البلد | باتريكس جلوبال"
                          maxLength={90}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span className={contentAr.meta_title.length > 60 ? 'text-orange-500' : ''}>{contentAr.meta_title.length}/90</span>
                          <span>الموصى به: 50-60 حرف</span>
                        </div>
                        <FieldError fieldKey="meta_title_ar" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          وصف ميتا (عربي)
                        </label>
                        <textarea
                          value={contentAr.meta_description}
                          onChange={(e) => handleFieldChange('meta_description_ar', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, meta_description_ar: true }))}
                          rows={3}
                          className={getInputClassName('meta_description_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="خدمات تأسيس شركات مهنية..."
                          maxLength={250}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span className={contentAr.meta_description.length > 160 ? 'text-orange-500' : ''}>{contentAr.meta_description.length}/250</span>
                          <span>الموصى به: 150-160 حرف</span>
                        </div>
                        <FieldError fieldKey="meta_description_ar" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          الكلمات المفتاحية (عربي)
                        </label>
                        <input
                          type="text"
                          value={contentAr.meta_keywords}
                          onChange={(e) => handleFieldChange('meta_keywords_ar', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, meta_keywords_ar: true }))}
                          className={getInputClassName('meta_keywords_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="تأسيس شركات، تأسيس الأعمال، التأسيس"
                          maxLength={255}
                        />
                        <p className="text-xs text-gray-500 mt-1">فصل الكلمات المفتاحية بفواصل</p>
                        <FieldError fieldKey="meta_keywords_ar" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          عنوان OG (عربي)
                        </label>
                        <input
                          type="text"
                          value={contentAr.og_title}
                          onChange={(e) => handleFieldChange('og_title_ar', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, og_title_ar: true }))}
                          className={getInputClassName('og_title_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="تأسيس الشركات في البلد - باتريكس جلوبال"
                          maxLength={90}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span className={contentAr.og_title.length > 60 ? 'text-orange-500' : ''}>{contentAr.og_title.length}/90</span>
                          <span>يُستخدم عند المشاركة على وسائل التواصل الاجتماعي</span>
                        </div>
                        <FieldError fieldKey="og_title_ar" />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          وصف OG (عربي)
                        </label>
                        <textarea
                          value={contentAr.og_description}
                          onChange={(e) => handleFieldChange('og_description_ar', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, og_description_ar: true }))}
                          rows={2}
                          className={getInputClassName('og_description_ar', 'w-full px-3 py-2 rounded-md shadow-sm focus:outline-none')}
                          placeholder="أسس عملك مع التوجيه المهني..."
                          maxLength={300}
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span className={contentAr.og_description.length > 200 ? 'text-orange-500' : ''}>{contentAr.og_description.length}/300</span>
                          <span>الموصى به: حتى 200 حرف</span>
                        </div>
                        <FieldError fieldKey="og_description_ar" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {(loading || isSubmitting) && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>{isSubmitting ? 'Validating...' : (page ? 'Update Page' : 'Create Page')}</span>
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ServicePageModal;